/**
 * AdminPage — Sprint 7 + Sprint 8
 * RSustain admin panel with:
 * - Platform-wide stats dashboard
 * - Access code management (list, generate, revoke, extend)
 * - User analytics — searchable user table + per-user activity drawer
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Navigate } from "react-router-dom";
import Navbar from "../components/layout/Navbar";
import { useAuth } from "../hooks/useAuth";
import {
  Users,
  Key,
  BarChart3,
  Award,
  Plus,
  RefreshCw,
  ShieldOff,
  CalendarPlus,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Copy,
  Check,
  ChevronLeft,
  ChevronRight,
  Activity,
  TrendingUp,
  Search,
  X,
  User,
  Mail,
  Clock,
  Leaf,
  BadgeCheck,
  FileSpreadsheet,
} from "lucide-react";
import toast from "react-hot-toast";
import "../styles/admin.css";

const API_BASE = "";
const PAGE_SIZE = 20;

/* ── API helpers ──────────────────────────────────────────── */
async function apiFetch(path, options = {}) {
  const token = localStorage.getItem("gs_token");
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || err.message || `Request failed (${res.status})`);
  }
  return res.json();
}

/* ── Shared sub-components ── */
function StatCard({ label, value, sub, icon: Icon, color, bg }) {
  return (
    <div className="adm-stat-card">
      <div className="adm-stat-icon" style={{ background: bg, color }}><Icon size={20} /></div>
      <div className="adm-stat-value">{value ?? "—"}</div>
      <div className="adm-stat-label">{label}</div>
      {sub && <div className="adm-stat-sub">{sub}</div>}
    </div>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };
  return (
    <button className="adm-copy-btn" onClick={handleCopy} title="Copy code">
      {copied ? <Check size={13} /> : <Copy size={13} />}
    </button>
  );
}

function ScorePill({ score }) {
  if (score == null) return <span className="adm-score-pill mid">—</span>;
  const cls = score >= 70 ? "high" : score >= 40 ? "mid" : "low";
  return <span className={`adm-score-pill ${cls}`}>{score}</span>;
}

/* ── Generate code form ── */
function GenerateCodeForm({ onGenerated }) {
  const today = new Date();
  const defaultExpiry = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate())
    .toISOString().split("T")[0];

  const [form, setForm] = useState({
    code: "",
    plan_type: "FREE_TRIAL",
    max_analyses: 2,
    expiry_date: defaultExpiry,
    issued_to_email: "",
    issued_to_gstin: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const PLAN_DEFAULTS = {
    FREE_TRIAL: { max_analyses: 2,    days: 30  },
    STARTER:    { max_analyses: 5,    days: 30  },
    BASIC:      { max_analyses: 10,   days: 31  },
    PRO:        { max_analyses: 9999, days: 31  },
    ENTERPRISE: { max_analyses: 9999, days: 365 },
  };

  const handlePlanChange = (plan) => {
    const d = PLAN_DEFAULTS[plan] || {};
    const expiry = new Date(today.getTime() + (d.days || 30) * 86400000).toISOString().split("T")[0];
    setForm((f) => ({ ...f, plan_type: plan, max_analyses: d.max_analyses || 5, expiry_date: expiry }));
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(""); setResult(null);
    try {
      const payload = {
        plan_type: form.plan_type,
        max_analyses: Number(form.max_analyses),
        expiry_date: form.expiry_date,
        ...(form.code.trim() ? { code: form.code.trim() } : {}),
        ...(form.issued_to_email.trim() ? { issued_to_email: form.issued_to_email.trim() } : {}),
        ...(form.issued_to_gstin.trim() ? { issued_to_gstin: form.issued_to_gstin.trim() } : {}),
        ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
      };
      const data = await apiFetch("/api/v1/admin/license/generate", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setResult(data);
      onGenerated?.();
      toast.success(`Access code generated: ${data.code}`);
      setForm((f) => ({ ...f, code: "", issued_to_email: "", issued_to_gstin: "", notes: "" }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="adm-gen-form" onSubmit={handleSubmit}>
      <div className="adm-field-grid">
        <div className="adm-field">
          <label className="adm-label">Plan Type</label>
          <select className="adm-input" value={form.plan_type} onChange={(e) => handlePlanChange(e.target.value)}>
            <option value="FREE_TRIAL">Free Trial (2 runs)</option>
            <option value="STARTER">Starter (5 runs)</option>
            <option value="BASIC">Basic (10 runs)</option>
            <option value="PRO">Pro (Unlimited)</option>
            <option value="ENTERPRISE">Enterprise (Unlimited)</option>
          </select>
        </div>
        <div className="adm-field">
          <label className="adm-label">Max Analyses</label>
          <input className="adm-input" type="number" min="1" max="99999"
            value={form.max_analyses} onChange={set("max_analyses")} />
        </div>
        <div className="adm-field">
          <label className="adm-label">Expiry Date</label>
          <input className="adm-input" type="date" value={form.expiry_date}
            min={today.toISOString().split("T")[0]} onChange={set("expiry_date")} required />
        </div>
        <div className="adm-field">
          <label className="adm-label">Custom Code <span className="adm-optional">(auto-generated if blank)</span></label>
          <input className="adm-input" value={form.code} onChange={set("code")}
            placeholder="e.g. GSU-2026-DEMO01" />
        </div>
        <div className="adm-field">
          <label className="adm-label">Issue to Email <span className="adm-optional">(optional)</span></label>
          <input className="adm-input" type="email" value={form.issued_to_email}
            onChange={set("issued_to_email")} placeholder="client@example.com" />
        </div>
        <div className="adm-field">
          <label className="adm-label">Bind to GSTIN <span className="adm-optional">(optional)</span></label>
          <input className="adm-input" value={form.issued_to_gstin} onChange={set("issued_to_gstin")}
            placeholder="e.g. 27AABCU9603R1ZX" />
        </div>
        <div className="adm-field adm-field-full">
          <label className="adm-label">Internal Notes <span className="adm-optional">(optional)</span></label>
          <input className="adm-input" value={form.notes} onChange={set("notes")}
            placeholder="e.g. Demo code for HDFC Bank pilot" />
        </div>
      </div>

      {error && <div className="adm-alert adm-alert-error"><AlertTriangle size={15} /> {error}</div>}

      {result && (
        <div className="adm-result-box">
          <CheckCircle2 size={16} />
          <div>
            <p className="adm-result-label">Access code generated</p>
            <div className="adm-result-code">
              <code>{result.code}</code>
              <CopyButton text={result.code} />
            </div>
            <p className="adm-result-meta">
              {result.plan_type} · {result.max_analyses} analyses · Expires {result.expiry_date}
            </p>
          </div>
        </div>
      )}

      <button className="adm-btn adm-btn-primary" type="submit" disabled={loading}>
        {loading ? <><Loader2 size={15} className="adm-spin" /> Generating…</> : <><Plus size={15} /> Generate Access Code</>}
      </button>
    </form>
  );
}

/* ── Access codes table ── */
function AccessCodesTable({ onRefreshNeeded }) {
  const [codes, setCodes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState(null);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const load = useCallback(async (p = 0) => {
    setLoading(true); setError("");
    try {
      const data = await apiFetch(`/api/v1/admin/access-codes?limit=${PAGE_SIZE}&offset=${p * PAGE_SIZE}`);
      setCodes(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(page); }, [page, load]);

  const handleRevoke = async (id) => {
    if (!window.confirm("Revoke this access code? The holder will lose access immediately.")) return;
    setActionLoading(id);
    try {
      await apiFetch(`/api/v1/admin/access-codes/${id}/revoke`, { method: "POST" });
      toast.success("Access code revoked.");
      load(page);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleExtend = async (id) => {
    const days = window.prompt("Extend by how many days?", "30");
    if (!days || isNaN(Number(days)) || Number(days) < 1) return;
    setActionLoading(id);
    try {
      await apiFetch(`/api/v1/admin/access-codes/${id}/extend?days=${Number(days)}`, { method: "POST" });
      toast.success(`Extended by ${Number(days)} days.`);
      load(page);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  function statusBadge(code) {
    if (!code.is_active) return <span className="adm-badge adm-badge-red">Revoked</span>;
    if (code.expiry_date && code.expiry_date < today) return <span className="adm-badge adm-badge-amber">Expired</span>;
    return <span className="adm-badge adm-badge-green">Active</span>;
  }

  function usagePct(code) {
    if (!code.max_analyses) return 0;
    return Math.min(100, Math.round((code.used_count / code.max_analyses) * 100));
  }

  if (loading && !codes.length) {
    return <div className="adm-loading"><Loader2 size={20} className="adm-spin" /> Loading access codes…</div>;
  }

  return (
    <div>
      {error && <div className="adm-alert adm-alert-error"><AlertTriangle size={15} /> {error}</div>}
      <div className="adm-table-scroll">
        <table className="adm-table">
          <thead>
            <tr>
              <th>Code</th><th>Plan</th><th>Issued To</th>
              <th>Usage</th><th>Expiry</th><th>Status</th><th>Notes</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {codes.length === 0 ? (
              <tr><td colSpan={8} className="adm-empty-row">No access codes found.</td></tr>
            ) : codes.map((c) => {
              const pct = usagePct(c);
              const isActing = actionLoading === c.id;
              return (
                <tr key={c.id}>
                  <td>
                    <div className="adm-code-cell">
                      <code className="adm-code">{c.code}</code>
                      <CopyButton text={c.code} />
                    </div>
                    {c.org_name && <div className="adm-cell-sub">{c.org_name}</div>}
                  </td>
                  <td><span className="adm-plan-badge">{c.plan_type}</span></td>
                  <td>
                    <div className="adm-issued-to">
                      {c.issued_to_email && <div>{c.issued_to_email}</div>}
                      {c.issued_to_gstin && <div className="adm-mono adm-small">{c.issued_to_gstin}</div>}
                      {!c.issued_to_email && !c.issued_to_gstin && <span className="adm-muted">Unbound</span>}
                    </div>
                  </td>
                  <td>
                    <div className="adm-usage-cell">
                      <div className="adm-usage-bar-wrap">
                        <div className="adm-usage-bar" style={{
                          width: `${pct}%`,
                          background: pct >= 90 ? "#ef4444" : pct >= 60 ? "#f59e0b" : "#10b981",
                        }} />
                      </div>
                      <span className="adm-usage-label">
                        {c.used_count}/{c.max_analyses >= 9999 ? "∞" : c.max_analyses}
                      </span>
                    </div>
                  </td>
                  <td>
                    <span className={c.expiry_date && c.expiry_date < today ? "adm-muted-red" : ""}>
                      {c.expiry_date || "—"}
                    </span>
                  </td>
                  <td>{statusBadge(c)}</td>
                  <td><span className="adm-notes">{c.notes || "—"}</span></td>
                  <td>
                    <div className="adm-action-row">
                      <button className="adm-action-btn adm-action-extend" onClick={() => handleExtend(c.id)}
                        disabled={isActing} title="Extend expiry">
                        {isActing ? <Loader2 size={13} className="adm-spin" /> : <CalendarPlus size={13} />}
                      </button>
                      {c.is_active && (
                        <button className="adm-action-btn adm-action-revoke" onClick={() => handleRevoke(c.id)}
                          disabled={isActing} title="Revoke code">
                          <ShieldOff size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="adm-pagination">
          <span className="adm-page-info">
            Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
          </span>
          <div className="adm-page-btns">
            <button className="adm-page-btn" onClick={() => setPage(p => p - 1)} disabled={page === 0}>
              <ChevronLeft size={16} />
            </button>
            <span className="adm-page-cur">{page + 1} / {totalPages}</span>
            <button className="adm-page-btn" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── User activity drawer ── */
function UserActivityDrawer({ accountId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const drawerRef = useRef(null);

  useEffect(() => {
    setLoading(true); setError(""); setData(null);
    apiFetch(`/api/v1/admin/users/${accountId}/activity`)
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [accountId]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const today = new Date().toISOString().split("T")[0];

  function fmtDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  }

  return (
    <>
      <div className="adm-drawer-overlay" onClick={onClose} />
      <div className="adm-drawer" ref={drawerRef}>
        <div className="adm-drawer-header">
          <div>
            <div className="adm-drawer-eyebrow">User Activity</div>
            {data && (
              <h2 className="adm-drawer-title">{data.account.email}</h2>
            )}
          </div>
          <button className="adm-drawer-close" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="adm-drawer-body">
          {loading && (
            <div className="adm-loading"><Loader2 size={20} className="adm-spin" /> Loading activity…</div>
          )}
          {error && (
            <div className="adm-alert adm-alert-error"><AlertTriangle size={14} /> {error}</div>
          )}

          {data && (
            <>
              {/* Account info */}
              <div className="adm-drawer-section">
                <div className="adm-drawer-section-title">Account</div>
                <div className="adm-user-meta-grid">
                  <div className="adm-user-meta-item">
                    <Mail size={13} className="adm-user-meta-icon" />
                    <span>{data.account.email}</span>
                  </div>
                  {data.account.full_name && (
                    <div className="adm-user-meta-item">
                      <User size={13} className="adm-user-meta-icon" />
                      <span>{data.account.full_name}</span>
                    </div>
                  )}
                  <div className="adm-user-meta-item">
                    <Clock size={13} className="adm-user-meta-icon" />
                    <span>Joined {fmtDate(data.account.created_at)}</span>
                  </div>
                </div>
              </div>

              {/* Summary stats */}
              <div className="adm-drawer-section">
                <div className="adm-drawer-section-title">Summary</div>
                <div className="adm-drawer-stats">
                  <div className="adm-drawer-stat">
                    <div className="adm-drawer-stat-value">{data.runs.length}</div>
                    <div className="adm-drawer-stat-label">Screening runs</div>
                  </div>
                  <div className="adm-drawer-stat">
                    <div className="adm-drawer-stat-value">
                      {data.runs.length
                        ? Math.round(data.runs.reduce((s, r) => s + (r.green_score || 0), 0) / data.runs.length)
                        : "—"}
                    </div>
                    <div className="adm-drawer-stat-label">Avg green score</div>
                  </div>
                  <div className="adm-drawer-stat">
                    <div className="adm-drawer-stat-value">
                      {data.runs.filter((r) => r.cert_status === "ACTIVE").length}
                    </div>
                    <div className="adm-drawer-stat-label">Active certs</div>
                  </div>
                  <div className="adm-drawer-stat">
                    <div className="adm-drawer-stat-value">{data.access_codes.length}</div>
                    <div className="adm-drawer-stat-label">Access codes</div>
                  </div>
                </div>
              </div>

              {/* Access codes */}
              {data.access_codes.length > 0 && (
                <div className="adm-drawer-section">
                  <div className="adm-drawer-section-title">Access Codes</div>
                  <div className="adm-drawer-codes">
                    {data.access_codes.map((c) => {
                      const expired = c.expiry_date && c.expiry_date < today;
                      const active = c.is_active && !expired;
                      return (
                        <div key={c.code} className={`adm-drawer-code-row${active ? " active" : ""}`}>
                          <div className="adm-drawer-code-left">
                            <code className="adm-code adm-code-sm">{c.code}</code>
                            <span className="adm-plan-badge" style={{ fontSize: "0.68rem" }}>{c.plan_type}</span>
                          </div>
                          <div className="adm-drawer-code-right">
                            <span className="adm-drawer-code-usage">
                              {c.used_count}/{c.max_analyses >= 9999 ? "∞" : c.max_analyses} used
                            </span>
                            <span className={`adm-badge ${active ? "adm-badge-green" : expired ? "adm-badge-amber" : "adm-badge-red"}`}>
                              {active ? "Active" : expired ? "Expired" : "Revoked"}
                            </span>
                          </div>
                          <div className="adm-drawer-code-expiry">Expires {fmtDate(c.expiry_date)}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Screening history */}
              <div className="adm-drawer-section">
                <div className="adm-drawer-section-title">
                  Screening History
                  {data.runs.length > 0 && (
                    <span className="adm-drawer-count">{data.runs.length} run{data.runs.length !== 1 ? "s" : ""}</span>
                  )}
                </div>
                {data.runs.length === 0 ? (
                  <div className="adm-empty-row" style={{ textAlign: "left", padding: "1rem 0" }}>
                    No screening runs yet.
                  </div>
                ) : (
                  <div className="adm-drawer-runs">
                    {data.runs.map((run) => (
                      <div key={run.id} className="adm-run-row">
                        <div className="adm-run-row-top">
                          <span className="adm-mono adm-small">{run.gstin}</span>
                          <ScorePill score={run.green_score} />
                        </div>
                        <div className="adm-run-row-mid">
                          <span className={`adm-run-status ${run.transition_status === "Eligible" ? "eligible" : "transition"}`}>
                            {run.transition_status || "—"}
                          </span>
                          <span className="adm-run-meta">
                            <Leaf size={11} /> {Number(run.total_carbon || 0).toFixed(2)} kg CO₂e
                          </span>
                          {run.cert_status === "ACTIVE" && (
                            <span className="adm-run-cert">
                              <BadgeCheck size={11} /> Cert issued
                            </span>
                          )}
                        </div>
                        <div className="adm-run-row-date">{fmtDate(run.created_at)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

/* ── Users table ── */
function UsersTable() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const searchTimer = useRef(null);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const load = useCallback(async (p = 0, q = "") => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE, offset: p * PAGE_SIZE });
      if (q.trim()) params.append("search", q.trim());
      const data = await apiFetch(`/api/v1/admin/users?${params}`);
      setUsers(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(0, search); }, [search, load]);
  useEffect(() => { load(page, search); }, [page]);  // eslint-disable-line

  function handleSearchChange(e) {
    setSearchInput(e.target.value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(e.target.value);
      setPage(0);
    }, 350);
  }

  function fmtDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  }

  function fmtRelative(iso) {
    if (!iso) return "Never";
    const diff = Date.now() - new Date(iso).getTime();
    const days = Math.floor(diff / 86400000);
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 30) return `${days}d ago`;
    if (days < 365) return `${Math.floor(days / 30)}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  }

  return (
    <div>
      {/* Search bar */}
      <div className="adm-users-toolbar">
        <div className="adm-search-wrap">
          <Search size={15} className="adm-search-icon" />
          <input
            className="adm-search-input"
            value={searchInput}
            onChange={handleSearchChange}
            placeholder="Search by email…"
          />
          {searchInput && (
            <button className="adm-search-clear" onClick={() => { setSearchInput(""); setSearch(""); setPage(0); }}>
              <X size={13} />
            </button>
          )}
        </div>
        <span className="adm-users-count">
          {total} user{total !== 1 ? "s" : ""}
        </span>
      </div>

      {error && <div className="adm-alert adm-alert-error"><AlertTriangle size={15} /> {error}</div>}

      {loading && !users.length ? (
        <div className="adm-loading"><Loader2 size={20} className="adm-spin" /> Loading users…</div>
      ) : (
        <div className="adm-table-scroll">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Joined</th>
                <th>Runs</th>
                <th>Last Active</th>
                <th>Active Plan</th>
                <th>Certs</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr><td colSpan={7} className="adm-empty-row">No users found.</td></tr>
              ) : users.map((u) => (
                <tr
                  key={u.id}
                  className="adm-user-row"
                  onClick={() => setSelectedUserId(u.id)}
                  title="Click to view activity"
                >
                  <td>
                    <div className="adm-user-email">{u.email}</div>
                    {u.full_name && <div className="adm-cell-sub">{u.full_name}</div>}
                  </td>
                  <td className="adm-small">{fmtDate(u.created_at)}</td>
                  <td>
                    <div className="adm-runs-cell">
                      <FileSpreadsheet size={12} />
                      <span>{u.total_runs}</span>
                    </div>
                  </td>
                  <td className="adm-small">{fmtRelative(u.last_run_at)}</td>
                  <td>
                    {u.active_plan
                      ? <span className="adm-plan-badge">{u.active_plan}</span>
                      : <span className="adm-muted">—</span>}
                  </td>
                  <td>
                    {u.active_certs > 0
                      ? <span className="adm-badge adm-badge-green">{u.active_certs}</span>
                      : <span className="adm-muted">0</span>}
                  </td>
                  <td>
                    <button className="adm-view-btn" onClick={(e) => { e.stopPropagation(); setSelectedUserId(u.id); }}>
                      View →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="adm-pagination">
          <span className="adm-page-info">
            Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
          </span>
          <div className="adm-page-btns">
            <button className="adm-page-btn" onClick={() => setPage(p => p - 1)} disabled={page === 0}>
              <ChevronLeft size={16} />
            </button>
            <span className="adm-page-cur">{page + 1} / {totalPages}</span>
            <button className="adm-page-btn" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {selectedUserId && (
        <UserActivityDrawer
          accountId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
        />
      )}
    </div>
  );
}

/* ── Waitlist Table ── */
function WaitlistTable() {
  const [items, setItems]   = useState([]);
  const [total, setTotal]   = useState(0);
  const [page, setPage]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState("");

  const totalPages = Math.ceil(total / PAGE_SIZE);

  useEffect(() => {
    setLoading(true); setError("");
    const params = new URLSearchParams({ page: page + 1, limit: PAGE_SIZE });
    apiFetch(`/api/v1/admin/waitlist?${params}`)
      .then((d) => { setItems(d.items || []); setTotal(d.total || 0); })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [page]);

  function fmtDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  }

  if (loading) return <div className="adm-loading"><Loader2 size={18} className="adm-spin" /> Loading waitlist…</div>;
  if (error)   return <div className="adm-alert adm-alert-error" style={{margin:"1rem"}}><AlertTriangle size={14}/> {error}</div>;

  return (
    <>
      <div className="adm-table-scroll">
        <table className="adm-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Email</th>
              <th>Name</th>
              <th>GSTIN Hint</th>
              <th>Signed Up</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={5} className="adm-empty-row">No waitlist entries yet.</td></tr>
            ) : items.map((item, i) => (
              <tr key={item.id}>
                <td className="adm-muted" style={{ width: 48 }}>{page * PAGE_SIZE + i + 1}</td>
                <td><span className="adm-mono adm-small">{item.email}</span></td>
                <td>{item.full_name || <span className="adm-muted">—</span>}</td>
                <td>
                  {item.gstin_hint
                    ? <code className="adm-code">{item.gstin_hint}</code>
                    : <span className="adm-muted">—</span>}
                </td>
                <td className="adm-muted adm-small">{fmtDate(item.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="adm-pagination">
          <span>{total} entries</span>
          <div className="adm-page-btns">
            <button className="adm-page-btn" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft size={14} />
            </button>
            <span className="adm-page-cur">Page {page + 1} / {totalPages}</span>
            <button className="adm-page-btn" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}


/* ── Main Page ── */
export default function AdminPage() {
  const { isAuthenticated } = useAuth();
  const isAdmin = localStorage.getItem("gs_is_admin") === "true";

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [codesKey, setCodesKey] = useState(0);

  const loadStats = useCallback(async () => {
    setStatsLoading(true); setStatsError("");
    try {
      const data = await apiFetch("/api/v1/admin/stats");
      setStats(data);
    } catch (err) {
      setStatsError(err.message);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  // Redirect non-admins (after all hooks)
  if (!isAuthenticated || !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleGenerated = () => setCodesKey(k => k + 1);

  const statCards = stats ? [
    { label: "Total Users",     value: stats.total_users,   sub: `+${stats.new_users_30d} this month`, icon: Users,    color: "#1a6b3c", bg: "#dcfce7" },
    { label: "Active Codes",    value: stats.active_codes,  sub: `${stats.total_codes} total issued`,  icon: Key,      color: "#0d9488", bg: "#ccfbf1" },
    { label: "Screening Runs",  value: stats.total_runs,    sub: `${stats.runs_30d} in last 30 days`,  icon: Activity, color: "#f59e0b", bg: "#fef9c3" },
    { label: "Avg Score (30d)", value: stats.avg_score_30d || "—", sub: "portfolio average",           icon: TrendingUp, color: "#6366f1", bg: "#ede9fe" },
    { label: "Active Certs",    value: stats.active_certs,  sub: "green certificates live",           icon: Award,    color: "#10b981", bg: "#d1fae5" },
  ] : [];

  const TABS = [
    { id: "overview",  label: "Overview"     },
    { id: "users",     label: "Users"        },
    { id: "codes",     label: "Access Codes" },
    { id: "generate",  label: "Issue Code"   },
    { id: "waitlist",  label: "Waitlist"     },
  ];

  return (
    <div className="adm-page">
      <Navbar />
      <main className="adm-main">
        <div className="adm-container">

          {/* ── Header ── */}
          <div className="adm-header">
            <div>
              <div className="adm-eyebrow">RSustain Admin</div>
              <h1 className="adm-title">Admin Panel</h1>
              <p className="adm-sub">Platform management, user analytics, and access code control</p>
            </div>
            <button className="adm-refresh-btn" onClick={loadStats} disabled={statsLoading}>
              <RefreshCw size={15} className={statsLoading ? "adm-spin" : ""} />
              Refresh
            </button>
          </div>

          {/* ── Stats cards ── */}
          {statsError && (
            <div className="adm-alert adm-alert-error"><AlertTriangle size={15} /> {statsError}</div>
          )}
          {statsLoading && !stats ? (
            <div className="adm-loading"><Loader2 size={20} className="adm-spin" /> Loading stats…</div>
          ) : stats && (
            <div className="adm-stats-grid">
              {statCards.map((c) => <StatCard key={c.label} {...c} />)}
            </div>
          )}

          {/* ── Tabs ── */}
          <div className="adm-tabs">
            {TABS.map((t) => (
              <button
                key={t.id}
                className={`adm-tab${activeTab === t.id ? " active" : ""}`}
                onClick={() => setActiveTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* ── Tab panels ── */}
          {activeTab === "overview" && (
            <div className="adm-panel">
              <div className="adm-panel-header">
                <h2 className="adm-panel-title">Platform Overview</h2>
                <p className="adm-panel-sub">Summary of platform activity and health.</p>
              </div>
              {stats && (
                <div className="adm-overview-grid">
                  <div className="adm-overview-card">
                    <div className="adm-ov-label">Users registered</div>
                    <div className="adm-ov-value">{stats.total_users}</div>
                    <div className="adm-ov-trend">+{stats.new_users_30d} in last 30 days</div>
                  </div>
                  <div className="adm-overview-card">
                    <div className="adm-ov-label">Screening runs total</div>
                    <div className="adm-ov-value">{stats.total_runs}</div>
                    <div className="adm-ov-trend">{stats.runs_30d} runs in last 30 days</div>
                  </div>
                  <div className="adm-overview-card">
                    <div className="adm-ov-label">Access codes issued</div>
                    <div className="adm-ov-value">{stats.total_codes}</div>
                    <div className="adm-ov-trend">{stats.active_codes} currently active</div>
                  </div>
                  <div className="adm-overview-card">
                    <div className="adm-ov-label">Green certificates</div>
                    <div className="adm-ov-value">{stats.active_certs}</div>
                    <div className="adm-ov-trend">active certificates</div>
                  </div>
                  <div className="adm-overview-card">
                    <div className="adm-ov-label">Auto-fetch waitlist</div>
                    <div className="adm-ov-value">{stats.waitlist_total ?? 0}</div>
                    <div className="adm-ov-trend">signups for GST Portal API</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "users" && (
            <div className="adm-panel">
              <div className="adm-panel-header">
                <h2 className="adm-panel-title">Users</h2>
                <p className="adm-panel-sub">All registered accounts. Click any row to view their full screening history and access codes.</p>
              </div>
              <UsersTable />
            </div>
          )}

          {activeTab === "codes" && (
            <div className="adm-panel">
              <div className="adm-panel-header">
                <h2 className="adm-panel-title">Access Codes</h2>
                <p className="adm-panel-sub">All issued access codes with usage and status. Use the Issue Code tab to generate new ones.</p>
              </div>
              <AccessCodesTable key={codesKey} onRefreshNeeded={() => setCodesKey(k => k + 1)} />
            </div>
          )}

          {activeTab === "generate" && (
            <div className="adm-panel">
              <div className="adm-panel-header">
                <h2 className="adm-panel-title">Issue New Access Code</h2>
                <p className="adm-panel-sub">Generate a code to grant a user or organisation access to GreenSetu screening.</p>
              </div>
              <GenerateCodeForm onGenerated={handleGenerated} />
            </div>
          )}

          {activeTab === "waitlist" && (
            <div className="adm-panel">
              <div className="adm-panel-header">
                <h2 className="adm-panel-title">Auto-fetch Waitlist</h2>
                <p className="adm-panel-sub">Users who have requested early access to the GST Portal auto-fetch feature.</p>
              </div>
              <WaitlistTable />
            </div>
          )}

        </div>
      </main>
    </div>
  );
}
